import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, PathRouteProps } from "react-router-dom";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";

const Login = React.lazy(
  () => import(/* webpackChunkName: "Login" */ "@pages/Login")
);
const Dashboard = React.lazy(
  () => import(/* webpackChunkName: "Dashboard" */ "@pages/Dashboard")
);
const Meetings = React.lazy(
  () => import(/* webpackChunkName: "Meetings" */ "@pages/Meetings")
);
const Configuration = React.lazy(
  () => import(/* webpackChunkName: "Configuration" */ "@pages/Configuration")
);
const Workflows = React.lazy(
  () =>
    import(/* webpackChunkName: "Workflows" */ "@pages/Configuration/Workflows")
);
const Assets = React.lazy(
  () => import(/* webpackChunkName: "Assets" */ "@pages/Assets")
);
const Rooms = React.lazy(
  () => import(/* webpackChunkName: "Rooms" */ "@pages/Assets/Rooms")
);
const Room = React.lazy(
  () => import(/* webpackChunkName: "Room" */ "@pages/Assets/Room")
);
const Device = React.lazy(
  () => import(/* webpackChunkName: "Device" */ "@pages/Assets/Device")
);
const Tickets = React.lazy(
  () => import(/* webpackChunkName: "Tickets" */ "@pages/Tickets")
);
const Devices = React.lazy(
  () => import(/* webpackChunkName: "Devices" */ "@pages/Assets/Devices")
);
const Accounts = React.lazy(
  () => import(/* webpackChunkName: "Accounts" */ "@pages/Assets/Accounts")
);

const Countries = React.lazy(
  () => import(/* webpackChunkName: "Countries" */ "@pages/Assets/Countries")
);
const Locations = React.lazy(
  () => import(/* webpackChunkName: "Locations" */ "@pages/Assets/Locations")
);
const Location = React.lazy(
  () => import(/* webpackChunkName: "Locations" */ "@pages/Assets/Location")
);
const BI = React.lazy(() => import(/* webpackChunkName: "BI" */ "@pages/BI"));
const WithLayout = React.lazy(
  () => import(/* webpackChunkName: "Layout" */ "@common/components/Layout")
);
const Triggers = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Triggers" */ "@pages/Configuration/Monitoring/Triggers"
    )
);
const MonitoringRestrictions = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MonitoringRestrictions" */ "@pages/Configuration/Monitoring/MonitoringRestrictions"
    )
);

const ExpirationDates = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExpirationDates" */ "@pages/Configuration/Monitoring/ExpirationDates"
    )
);

const Scheduling = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExpirationDates" */ "@pages/Configuration/Scheduling"
    )
);

const Security = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExpirationDates" */ "@pages/Configuration/Security"
    )
);

const HelpAndResources = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HelpAndResources" */ "@pages/Configuration/HelpAndResources"
    )
);

const Users = React.lazy(
  () => import(/* webpackChunkName: "Users" */ "@pages/Configuration/Users")
);

const Messaging = React.lazy(
  () =>
    import(/* webpackChunkName: "Users" */ "@pages/Configuration/Notifications")
);

const AdapterConfiguration = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AdapterConfiguration" */ "@pages/Configuration/Tickets/TicketsAdapter/Container"
    )
);

const ClosingPolicies = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ClosingPolicies" */ "@pages/Configuration/Tickets/ClosingPolicies/Container"
    )
);

const RoomSweep = React.lazy(
  () => import(/* webpackChunkName: "Room Sweep" */ "@pages/RoomSweep")
);
const AccountDetail = React.lazy(
  () => import(/* webpackChunkName: "Account Detail" */ "@pages/Assets/Account")
);

const WorkflowsResultsLog = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Workflows Results Log" */ "@pages/Configuration/Workflows/WorkflowsResultsLog"
    )
);

const WorkflowTriggers = React.lazy(
  () =>
    import(
      /* webpackChunkName: "workflow Triggers" */ "@pages/Configuration/Workflows/Triggers"
    )
);

const Microservices = React.lazy(
  () =>
    import(/* webpackChunkName: "versions aggregator" */ "@pages/Microservices")
);

import Suspense from "@common/components/Suspense";
import MeetingDetail from "@pages/Meeting";
import Audit from "@pages/BI/Audit";
import { EntityTypeList } from "@pages/BI/Audit/const";
import { DeviceCatalog } from "@pages/Configuration/DeviceCatalog";
import RolesAndCapabilities from "@pages/Configuration/RolesAndCapabilities";
import LogoutSuccess from "@pages/Login/SAMLpages/LogoutSuccess";
import LogoutFailure from "@pages/Login/SAMLpages/LogoutFailure";
import NotificationProvider from "@common/components/Notification";
import { CatalogProvider } from "@common/components/CatalogProvider";
import { setDefaultTimeZone } from "@common/utils/localTime.utils";
import { getCurrentUser } from "@services/user";
import { useRealTimeData } from "@common/components/Header/useRealTimeData";
import { useLocalStorage } from "@services/common/hooks/useLocalStorage";
import { TICKETS_COUNTS } from "@common/constants";
import { ReportsProvider } from "@pages/BI/context";
import { GlobalPreferencesProvider } from "@common/components/GlobalPreferences";
import RouteAccessChecker from "../RouteAccessChecker";

type AppRouterProps = {};

const AppRouter = () => {
  const [user] = useAuthenticatedUser();
  const isLoggedIn = user !== undefined;

  if (isLoggedIn) setDefaultTimeZone(user?.details?.timeZone);

  const routesWithLayout: PathRouteProps[] = [
    { path: "bi/*", element: <BI /> },
    { path: "meetings", element: <Meetings /> },
    { path: "meetings/:meetingid", element: <MeetingDetail /> },
    { path: "configuration", element: <Configuration /> },
    { path: "configuration/workflows/*", element: <Workflows /> },
    {
      path: "configuration/workflows/logs/:id",
      element: <WorkflowsResultsLog />,
    },
    {
      path: "configuration/workflows/:workflowid/triggers",
      element: <WorkflowTriggers />,
    },
    { path: "configuration/triggers", element: <Triggers /> },
    { path: "configuration/device-catalog", element: <DeviceCatalog /> },
    {
      path: "configuration/audits",
      element: <Audit entityType={EntityTypeList.PROPERTIES} />,
    },
    {
      path: "configuration/monitoringRestrictions",
      element: <MonitoringRestrictions />,
    },
    {
      path: "configuration/scheduling",
      element: <Scheduling />,
    },
    {
      path: "configuration/expirationDates",
      element: <ExpirationDates />,
    },
    {
      path: "configuration/rolesAndCapabilities",
      element: <RolesAndCapabilities />,
    },
    { path: "configuration/users", element: <Users /> },
    { path: "/configuration/messaging", element: <Messaging /> },
    {
      path: "configuration/adapterConfiguration",
      element: <AdapterConfiguration />,
    },
    {
      path: "configuration/closingPolicies",
      element: <ClosingPolicies />,
    },
    {
      path: "configuration/security",
      element: <Security />,
    },
    {
      path: "configuration/help-and-resources",
      element: <HelpAndResources />,
    },
    { path: "assets", element: <Assets /> },
    { path: "assets/rooms", element: <Rooms /> },
    { path: "assets/rooms/:roomid/*", element: <Room /> },
    { path: "assets/devices", element: <Devices /> },
    { path: "assets/devices/:deviceid/*", element: <Device /> },
    { path: "assets/accounts", element: <Accounts /> },
    {
      path: "assets/accounts/:accountid/*",
      element: <AccountDetail />,
    },
    { path: "assets/countries", element: <Countries /> },
    { path: "assets/locations", element: <Locations /> },
    { path: "assets/locations/:locationid/*", element: <Location /> },
    { path: "tickets", element: <Tickets /> },
    { path: "tickets/:ticketid", element: <Tickets /> },
    { path: "roomsweep/*", element: <RoomSweep /> },
    { path: "microservices", element: <Microservices /> },
  ];
  const realTimeData = useRealTimeData();
  const [ticketCountsLS, setTicketCountsLS] =
    useLocalStorage<RealTimeTicketLocalStorage>(TICKETS_COUNTS);

  const getTicketArrowDirection = (
    newCount: number,
    prevCount?: number,
    prevDirection?: RealTimeTicketArrow
  ): RealTimeTicketArrow | undefined => {
    if (prevCount !== undefined) {
      if (prevDirection !== undefined) {
        if (newCount > prevCount) return "ToUp";
        else if (newCount < prevCount) return "ToDown";
        else return prevDirection;
      } else {
        if (newCount > prevCount) return "Up";
        if (newCount < prevCount) return "Down";
      }
    }
    return undefined;
  };

  useEffect(() => {
    if (realTimeData.ticketCount?.data) {
      const status: RealTimeTicketLocalStorage = {};
      realTimeData.ticketCount?.data.forEach((data) => {
        const prevState =
          localStorage.getItem(TICKETS_COUNTS) && ticketCountsLS
            ? ticketCountsLS[
                data.label.text as keyof RealTimeTicketLocalStorage
              ]
            : undefined;
        const newCount = data.data ?? 0;
        status[data.label.text as keyof RealTimeTicketLocalStorage] = {
          count: newCount,
          direction: getTicketArrowDirection(
            newCount,
            prevState?.count,
            prevState?.direction
          ),
        };
      });
      setTicketCountsLS(status);
    }
  }, [realTimeData.ticketCount?.data]);

  const getAuthenticatedRoutes = () => (
    <>
      {routesWithLayout.map((route) => (
        <Route
          key={route.path}
          {...route}
          element={
            <Suspense>
              <WithLayout realTimeData={realTimeData}>
                <Suspense>{route.element}</Suspense>
              </WithLayout>
            </Suspense>
          }
        />
      ))}
      <Route path="logoutsuccess" element={<LogoutSuccess />} />
      <Route path="logoutfailure" element={<LogoutFailure />} />
      <Route
        path="/*"
        element={
          <Suspense>
            <Dashboard />
          </Suspense>
        }
      />
    </>
  );
  return (
    <BrowserRouter>
      <NotificationProvider>
        {isLoggedIn ? (
          <CatalogProvider>
            <ReportsProvider>
              <GlobalPreferencesProvider>
                <Routes>{getAuthenticatedRoutes()}</Routes>
                <RouteAccessChecker />
              </GlobalPreferencesProvider>
            </ReportsProvider>
          </CatalogProvider>
        ) : (
          <Routes>
            <Route
              path="/*"
              element={
                <Suspense>
                  <Login />
                </Suspense>
              }
            />
          </Routes>
        )}
      </NotificationProvider>
    </BrowserRouter>
  );
};

/**
 * Used to silently log out the user when first loading the page
 */
const AppRouterAuthChecker = () => {
  const [user] = useAuthenticatedUser();
  const [authChecked, setAuthChecked] = useState(user === undefined);
  const testAuth = async () => getCurrentUser();
  useEffect(() => {
    if (user) testAuth().finally(() => setAuthChecked(true));
  }, []);
  if (authChecked) return <AppRouter />;
  else return <></>;
};

export default AppRouterAuthChecker;
